import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  LockOpen as LockOpenIcon,
  Visibility as VisibilityIcon,
  Note as NoteIcon,
  FileDownload as FileDownloadIcon,
  CloudUpload as CloudUploadIcon,
  CompareArrows as CompareArrowsIcon,
  PlaylistAdd as PlaylistAddIcon,
  FindInPage as FindInPageIcon,
  InfoOutlined as InfoOutlinedIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon,
  Lock as LockIcon,
  RateReview as RateReviewIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { useApi } from '../contexts/ApiContext';

// Status chip colors - enhanced workflow
const statusColors = {
  OPEN: { color: 'info', icon: <LockOpenIcon />, label: 'Open' },
  PENDING: { color: 'warning', icon: <ScheduleIcon />, label: 'Pending Review' },
  REVIEW: { color: 'secondary', icon: <RateReviewIcon />, label: 'In Review' },
  APPROVED: { color: 'primary', icon: <CheckCircleIcon />, label: 'Approved' },
  RECONCILED: { color: 'success', icon: <CheckCircleIcon />, label: 'Reconciled' },
  VARIANCE: { color: 'error', icon: <WarningIcon />, label: 'Variance' },
  LOCKED: { color: 'default', icon: <LockIcon />, label: 'Locked' }
};

const SettlementBatches = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchTransactions, setBatchTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [reconcileData, setReconcileData] = useState({
    processorTransactionCount: '',
    processorDispensedTotal: '',
    processorSurchargeTotal: '',
    notes: ''
  });
  const [tabValue, setTabValue] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [processorTransactions, setProcessorTransactions] = useState([]);
  const [processorLoading, setProcessorLoading] = useState(false);
  const [reconcileDepositsLoading, setReconcileDepositsLoading] = useState(false);
  const [reconcileDepositsResult, setReconcileDepositsResult] = useState(null);
  const [reconcileDepositsDialogOpen, setReconcileDepositsDialogOpen] = useState(false);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchDetailsLoading, setBatchDetailsLoading] = useState(false);
  const [batchDetailsDialogOpen, setBatchDetailsDialogOpen] = useState(false);
  const [addingMissing, setAddingMissing] = useState(false);
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  const [importFiles, setImportFiles] = useState([]);

  const { apiCall } = useApi();

  // Toggle terminal breakdown expansion
  const toggleExpanded = (batchId) => {
    setExpandedBatchId(expandedBatchId === batchId ? null : batchId);
  };

  // Parse terminal breakdown JSON - prefer live data from view, fallback to stored
  const parseTerminalBreakdown = (batch) => {
    // First try live_terminal_details from the reconciliation view
    const liveDetails = batch.live_terminal_details;
    if (liveDetails) {
      try {
        const details = typeof liveDetails === 'string' ? JSON.parse(liveDetails) : liveDetails;
        if (Array.isArray(details)) {
          return details.map((term) => ({
            terminalId: term.terminal,
            gh_terminals: term.gh_terminals,
            proc_txns: term.proc_txns,
            proc_disp: term.proc_disp,
            our_txns: term.our_txns,
            our_disp: term.our_disp,
            variance: term.variance
          }));
        }
      } catch (e) {
        console.warn('Failed to parse live_terminal_details:', e);
      }
    }

    // Fallback to stored terminal_breakdown
    if (!batch.terminal_breakdown) return [];
    try {
      const breakdown = typeof batch.terminal_breakdown === 'string'
        ? JSON.parse(batch.terminal_breakdown)
        : batch.terminal_breakdown;
      return Object.entries(breakdown).map(([terminalId, data]) => ({
        terminalId,
        ...data
      }));
    } catch (e) {
      console.error('Failed to parse terminal breakdown:', e);
      return [];
    }
  };

  // Fetch batches
  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      if (statusFilter) params.append('status', statusFilter);
      if (locationFilter) params.append('location', locationFilter);

      const response = await apiCall(`/api/v1/settlement-batches?${params.toString()}`);
      setBatches(response.batches || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  }, [apiCall, startDate, endDate, statusFilter, locationFilter]);

  // Removed auto-loading - user must click Refresh to load batches

  
  // Fetch transactions for a batch
  const fetchBatchTransactions = async (batchId) => {
    try {
      setTransactionsLoading(true);
      const response = await apiCall(`/api/v1/settlement-batches/${batchId}/transactions`);
      setBatchTransactions(response.transactions || []);
      setSelectedBatch(response.batch);
    } catch (err) {
      setError(err.message || 'Failed to fetch transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Handle reconciliation
  const handleReconcile = async () => {
    try {
      await apiCall(`/api/v1/settlement-batches/${selectedBatch.id}/reconcile`, 'POST', {
        batchId: selectedBatch.id,
        processorTransactionCount: parseInt(reconcileData.processorTransactionCount) || null,
        processorDispensedTotal: parseFloat(reconcileData.processorDispensedTotal) || null,
        processorSurchargeTotal: parseFloat(reconcileData.processorSurchargeTotal) || null,
        notes: reconcileData.notes
      });
      setReconcileDialogOpen(false);
      fetchBatches();
      if (selectedBatch) {
        fetchBatchTransactions(selectedBatch.id);
      }
    } catch (err) {
      setError(err.message || 'Failed to reconcile batch');
    }
  };

  // Handle file upload for processor transactions
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      setUploading(true);
      setUploadResult(null);
      setError(null);

      // Get presigned URL for upload
      const presignedResponse = await apiCall('/api/v1/upload', 'POST', {
        requestType: 'presignedUrl',
        fileName: `processor-${Date.now()}-${file.name}`,
        fileType: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        fileSize: file.size
      });

      // Upload file to S3 using presigned URL
      const uploadResponse = await fetch(presignedResponse.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Trigger the processor-transaction-import Lambda
      const importResponse = await apiCall('/api/v1/processor-transactions/import', 'POST', {
        bucket: 'settlement-emails-myview',
        key: presignedResponse.s3Key
      });

      setUploadResult(importResponse);

      // Refresh batches after import
      fetchBatches();
    } catch (err) {
      setError(err.message || 'Failed to upload and process file');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Reconcile batches with settlement deposits
  const handleReconcileWithDeposits = async () => {
    try {
      setReconcileDepositsLoading(true);
      setError(null);

      const response = await apiCall('/api/v1/settlement-batches/reconcile-deposits', 'POST', {
        settlementDate: startDate ? format(startDate, 'yyyy-MM-dd') : null,
        endDate: endDate ? format(endDate, 'yyyy-MM-dd') : null
      });

      setReconcileDepositsResult(response);
      setReconcileDepositsDialogOpen(true);
      // Refresh batches to show updated status
      fetchBatches();
    } catch (err) {
      setError(err.message || 'Failed to reconcile with deposits');
    } finally {
      setReconcileDepositsLoading(false);
    }
  };

  // Fetch batch reconciliation details
  const fetchBatchDetails = async (batch) => {
    try {
      setBatchDetailsLoading(true);
      setBatchDetailsDialogOpen(true);
      setSelectedBatch(batch);
      setError(null);

      const response = await apiCall(`/api/v1/settlement-batches/${batch.id}/reconcile`, 'POST', { batchId: batch.id, getDetails: true });

      setBatchDetails(response);
    } catch (err) {
      setError(err.message || 'Failed to fetch batch details');
      setBatchDetailsDialogOpen(false);
    } finally {
      setBatchDetailsLoading(false);
    }
  };

  // Add missing transactions
  const handleAddMissing = async () => {
    if (!selectedBatch) return;

    try {
      setAddingMissing(true);
      setError(null);

      const response = await apiCall(`/api/v1/settlement-batches/${selectedBatch.id}/reconcile`, 'POST', { batchId: selectedBatch.id, getDetails: true, addMissing: true });

      setBatchDetails(response);
      // Refresh batches to show updated totals
      fetchBatches();
    } catch (err) {
      setError(err.message || 'Failed to add missing transactions');
    } finally {
      setAddingMissing(false);
    }
  };

  // Fetch processor transactions
  const fetchProcessorTransactions = async (calendarDate) => {
    try {
      setProcessorLoading(true);
      const params = new URLSearchParams();
      if (calendarDate) params.append('calendarDate', calendarDate);

      const response = await apiCall(`/api/v1/processor-transactions?${params.toString()}`);
      setProcessorTransactions(response.transactions || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch processor transactions');
    } finally {
      setProcessorLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Calculate summary stats
  const summaryStats = {
    total: batches.length,
    open: batches.filter(b => b.status === 'OPEN').length,
    pending: batches.filter(b => b.status === 'PENDING').length,
    review: batches.filter(b => b.status === 'REVIEW').length,
    reconciled: batches.filter(b => b.status === 'RECONCILED' || b.status === 'APPROVED').length,
    variance: batches.filter(b => b.status === 'VARIANCE').length,
    locked: batches.filter(b => b.is_locked).length,
    totalFileDispensed: batches.reduce((sum, b) => sum + parseFloat(b.processor_dispensed_total || 0), 0),
    totalCapturedDispensed: batches.reduce((sum, b) => sum + parseFloat(b.our_dispensed_total || 0), 0),
    totalVariance: batches.reduce((sum, b) => {
      const fileAmt = parseFloat(b.processor_dispensed_total || 0);
      const capturedAmt = parseFloat(b.our_dispensed_total || 0);
      return sum + (fileAmt - capturedAmt);
    }, 0)
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Settlement Batch Reconciliation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Three-way comparison: <strong>FILE</strong> (processor import source of truth) vs <strong>IMPORTED</strong> (processor_transactions) vs <strong>CAPTURED</strong> (settlement_transactions)
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Status Cards - Row 1 */}
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">Total Batches</Typography>
                <Typography variant="h4">{summaryStats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ borderLeft: '4px solid #ff9800' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">Pending Review</Typography>
                <Typography variant="h4" color="warning.main">{summaryStats.pending + summaryStats.review}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ borderLeft: '4px solid #4caf50' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">Reconciled</Typography>
                <Typography variant="h4" color="success.main">{summaryStats.reconciled}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ borderLeft: '4px solid #f44336' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">Variance</Typography>
                <Typography variant="h4" color="error.main">{summaryStats.variance}</Typography>
              </CardContent>
            </Card>
          </Grid>
          {/* Totals Cards - Row 2 */}
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>File Total</Typography>
                <Typography variant="h6">{formatCurrency(summaryStats.totalFileDispensed)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: summaryStats.totalVariance === 0 ? 'success.light' : 'error.light', color: summaryStats.totalVariance === 0 ? 'success.contrastText' : 'error.contrastText' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>Net Variance</Typography>
                <Typography variant="h6">{formatCurrency(summaryStats.totalVariance)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="RECONCILED">Reconciled</MenuItem>
                    <MenuItem value="VARIANCE">Variance</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Location"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  placeholder="Filter by location..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchBatches}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Import File'}
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                    />
                  </Button>
                  <Tooltip title="Compare batches with ACH settlement deposits">
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={reconcileDepositsLoading ? <CircularProgress size={20} /> : <CompareArrowsIcon />}
                      onClick={handleReconcileWithDeposits}
                      disabled={reconcileDepositsLoading}
                    >
                      {reconcileDepositsLoading ? 'Reconciling...' : 'Reconcile Deposits'}
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Import Result */}
        {uploadResult && (
          <Alert
            severity={uploadResult.matching?.unmatched > 0 ? 'warning' : 'success'}
            sx={{ mb: 3 }}
            onClose={() => setUploadResult(null)}
          >
            <Typography variant="subtitle2">
              Import Complete: {uploadResult.fileName}
            </Typography>
            <Typography variant="body2">
              Parsed: {uploadResult.totalParsed} |
              Inserted: {uploadResult.inserted} |
              Duplicates: {uploadResult.duplicates}
            </Typography>
            {uploadResult.matching && (
              <Typography variant="body2">
                Matched: {uploadResult.matching.matched} |
                Partial: {uploadResult.matching.partial} |
                Unmatched: {uploadResult.matching.unmatched}
              </Typography>
            )}
          </Alert>
        )}

        {/* Batches Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : batches.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No batches found for the selected filters
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }}></TableCell>
                      <TableCell>Settlement Date</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                        <Tooltip title="Source of truth - parsed from import file">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <DescriptionIcon fontSize="small" />
                            FILE (Source)
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" colSpan={2} sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                        <Tooltip title="What was imported into processor_transactions">
                          <Box>IMPORTED</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" colSpan={2} sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
                        <Tooltip title="What we captured in settlement_transactions">
                          <Box>CAPTURED</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">Variance</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Txns</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Dispensed</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Txns</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Dispensed</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Txns</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Dispensed</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batches.map((batch) => {
                      const terminalBreakdown = parseTerminalBreakdown(batch);
                      const isExpanded = expandedBatchId === batch.id;
                      const hasTerminalData = terminalBreakdown.length > 0;

                      // Calculate variance using live values
                      // FILE = stored processor_* values (immutable from import)
                      // IMPORTED = live from processor_transactions
                      // CAPTURED = live from settlement_transactions (should match transaction reports)
                      const fileDispensed = parseFloat(batch.processor_dispensed_total || 0);
                      const importedDispensed = parseFloat(batch.imported_dispensed_total || batch.processor_dispensed_total || 0);
                      const capturedDispensed = parseFloat(batch.captured_dispensed_total || batch.our_dispensed_total || 0);
                      // Variance between IMPORTED and CAPTURED (reconciliation variance)
                      const variance = importedDispensed - capturedDispensed;

                      return (
                        <React.Fragment key={batch.id}>
                          <TableRow
                            hover
                            sx={{
                              backgroundColor: batch.status === 'VARIANCE' ? 'rgba(244, 67, 54, 0.08)' :
                                              batch.is_locked ? 'rgba(0, 0, 0, 0.04)' : 'inherit'
                            }}
                          >
                            <TableCell>
                              {hasTerminalData && (
                                <IconButton size="small" onClick={() => toggleExpanded(batch.id)}>
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              {batch.settlement_date?.split('T')[0]}
                              {batch.import_file_id && (
                                <Tooltip title={`Import File #${batch.import_file_id}`}>
                                  <DescriptionIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>{batch.location_name}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  icon={statusColors[batch.status]?.icon}
                                  label={statusColors[batch.status]?.label || batch.status}
                                  color={statusColors[batch.status]?.color || 'default'}
                                />
                                {batch.is_locked && (
                                  <Tooltip title={`Locked: ${batch.lock_reason || 'No reason provided'}`}>
                                    <LockIcon fontSize="small" color="action" />
                                  </Tooltip>
                                )}
                                {batch.reviewed_by && (
                                  <Tooltip title={`Reviewed by ${batch.reviewed_by}`}>
                                    <RateReviewIcon fontSize="small" color="primary" />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            {/* FILE (Source of Truth - stored at import time) */}
                            <TableCell align="right" sx={{ bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                              {batch.processor_transaction_count !== null ? batch.processor_transaction_count : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                              {batch.processor_dispensed_total !== null
                                ? formatCurrency(batch.processor_dispensed_total)
                                : '-'}
                            </TableCell>
                            {/* IMPORTED (live from processor_transactions) */}
                            <TableCell align="right" sx={{ bgcolor: 'rgba(156, 39, 176, 0.04)' }}>
                              {batch.imported_transaction_count !== null
                                ? batch.imported_transaction_count
                                : (batch.processor_transaction_count !== null ? batch.processor_transaction_count : '-')}
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'rgba(156, 39, 176, 0.04)' }}>
                              {batch.imported_dispensed_total !== null
                                ? formatCurrency(batch.imported_dispensed_total)
                                : (batch.processor_dispensed_total !== null
                                    ? formatCurrency(batch.processor_dispensed_total)
                                    : '-')}
                            </TableCell>
                            {/* CAPTURED (live from settlement_transactions - matches transaction reports) */}
                            <TableCell align="right" sx={{ bgcolor: 'rgba(2, 136, 209, 0.04)' }}>
                              {batch.captured_transaction_count !== null
                                ? batch.captured_transaction_count
                                : batch.our_transaction_count}
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'rgba(2, 136, 209, 0.04)' }}>
                              {formatCurrency(batch.captured_dispensed_total !== null
                                ? batch.captured_dispensed_total
                                : batch.our_dispensed_total)}
                            </TableCell>
                            {/* Variance */}
                            <TableCell
                              align="right"
                              sx={{
                                color: variance !== 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}
                            >
                              {formatCurrency(variance)}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Transactions">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    fetchBatchTransactions(batch.id);
                                    setTabValue(1);
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reconciliation Details">
                                <IconButton
                                  size="small"
                                  onClick={() => fetchBatchDetails(batch)}
                                  color={batch.status === 'VARIANCE' ? 'error' : 'default'}
                                >
                                  <FindInPageIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Manual Reconcile">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedBatch(batch);
                                      setReconcileData({
                                        processorTransactionCount: batch.processor_transaction_count || '',
                                        processorDispensedTotal: batch.processor_dispensed_total || '',
                                        processorSurchargeTotal: batch.processor_surcharge_total || '',
                                        notes: ''
                                      });
                                      setReconcileDialogOpen(true);
                                    }}
                                    disabled={batch.status === 'OPEN' || batch.is_locked}
                                  >
                                    <NoteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          {/* Terminal Breakdown Expansion */}
                          {isExpanded && hasTerminalData && (
                            <TableRow>
                              <TableCell colSpan={12} sx={{ py: 0, bgcolor: 'grey.50' }}>
                                <Box sx={{ p: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Terminal Breakdown
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Processor Terminal</TableCell>
                                        <TableCell>GH Terminals</TableCell>
                                        <TableCell align="right">Proc Txns</TableCell>
                                        <TableCell align="right">Proc Dispensed</TableCell>
                                        <TableCell align="right">Our Txns</TableCell>
                                        <TableCell align="right">Our Dispensed</TableCell>
                                        <TableCell align="right">Variance</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {terminalBreakdown.map((term) => {
                                        const termVariance = (parseFloat(term.proc_disp || 0) - parseFloat(term.our_disp || 0));
                                        return (
                                          <TableRow key={term.terminalId}>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{term.terminalId}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                              {Array.isArray(term.gh_terminals) ? term.gh_terminals.join(', ') : term.gh_terminals || '-'}
                                            </TableCell>
                                            <TableCell align="right">{term.proc_txns}</TableCell>
                                            <TableCell align="right">{formatCurrency(term.proc_disp)}</TableCell>
                                            <TableCell align="right">{term.our_txns}</TableCell>
                                            <TableCell align="right">{formatCurrency(term.our_disp)}</TableCell>
                                            <TableCell
                                              align="right"
                                              sx={{
                                                color: termVariance !== 0 ? 'error.main' : 'success.main',
                                                fontWeight: termVariance !== 0 ? 'bold' : 'normal'
                                              }}
                                            >
                                              {formatCurrency(termVariance)}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Batch Transactions Dialog */}
        {selectedBatch && batchTransactions.length > 0 && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Transactions for {selectedBatch.location_name} - {selectedBatch.settlement_date}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedBatch(null);
                    setBatchTransactions([]);
                  }}
                >
                  Close
                </Button>
              </Box>
              {transactionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Terminal</TableCell>
                        <TableCell>Card</TableCell>
                        <TableCell align="right">Dispensed</TableCell>
                        <TableCell align="right">Surcharge</TableCell>
                        <TableCell>RRN</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batchTransactions.map((txn, idx) => (
                        <TableRow key={txn.id || idx} hover>
                          <TableCell>{txn.transaction_date}</TableCell>
                          <TableCell>{txn.transaction_time}</TableCell>
                          <TableCell>{txn.terminal_id}</TableCell>
                          <TableCell>****{txn.card_last_4}</TableCell>
                          <TableCell align="right">{formatCurrency(txn.dispensed_amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(txn.surcharge_amount)}</TableCell>
                          <TableCell>{txn.rrn || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reconcile Dialog */}
        <Dialog open={reconcileDialogOpen} onClose={() => setReconcileDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Reconcile Batch
            {selectedBatch && (
              <Typography variant="body2" color="text.secondary">
                {selectedBatch.location_name} - {selectedBatch.settlement_date}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Three-column comparison */}
              <Grid item xs={12}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>FILE (Source)</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>CAPTURED</TableCell>
                      <TableCell align="right">VARIANCE</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Transactions</strong></TableCell>
                      <TableCell align="right">{selectedBatch?.processor_transaction_count || '-'}</TableCell>
                      <TableCell align="right">{selectedBatch?.our_transaction_count}</TableCell>
                      <TableCell align="right" sx={{
                        color: (selectedBatch?.processor_transaction_count || 0) !== (selectedBatch?.our_transaction_count || 0) ? 'error.main' : 'success.main',
                        fontWeight: 'bold'
                      }}>
                        {(selectedBatch?.processor_transaction_count || 0) - (selectedBatch?.our_transaction_count || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Dispensed</strong></TableCell>
                      <TableCell align="right">{formatCurrency(selectedBatch?.processor_dispensed_total)}</TableCell>
                      <TableCell align="right">{formatCurrency(selectedBatch?.our_dispensed_total)}</TableCell>
                      <TableCell align="right" sx={{
                        color: parseFloat(selectedBatch?.processor_dispensed_total || 0) !== parseFloat(selectedBatch?.our_dispensed_total || 0) ? 'error.main' : 'success.main',
                        fontWeight: 'bold'
                      }}>
                        {formatCurrency((selectedBatch?.processor_dispensed_total || 0) - (selectedBatch?.our_dispensed_total || 0))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Surcharge</strong></TableCell>
                      <TableCell align="right">{formatCurrency(selectedBatch?.processor_surcharge_total)}</TableCell>
                      <TableCell align="right">{formatCurrency(selectedBatch?.our_surcharge_total)}</TableCell>
                      <TableCell align="right" sx={{
                        color: parseFloat(selectedBatch?.processor_surcharge_total || 0) !== parseFloat(selectedBatch?.our_surcharge_total || 0) ? 'error.main' : 'success.main',
                        fontWeight: 'bold'
                      }}>
                        {formatCurrency((selectedBatch?.processor_surcharge_total || 0) - (selectedBatch?.our_surcharge_total || 0))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
              {/* Manual adjustment fields */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Manual Adjustment (override file values if needed)
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Txn Count"
                  type="number"
                  value={reconcileData.processorTransactionCount}
                  onChange={(e) => setReconcileData({ ...reconcileData, processorTransactionCount: e.target.value })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Dispensed"
                  type="number"
                  value={reconcileData.processorDispensedTotal}
                  onChange={(e) => setReconcileData({ ...reconcileData, processorDispensedTotal: e.target.value })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Surcharge"
                  type="number"
                  value={reconcileData.processorSurchargeTotal}
                  onChange={(e) => setReconcileData({ ...reconcileData, processorSurchargeTotal: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes (required to mark as reconciled with variance)"
                  value={reconcileData.notes}
                  onChange={(e) => setReconcileData({ ...reconcileData, notes: e.target.value })}
                  placeholder="Explain the variance reason..."
                  helperText="Add a note to acknowledge the variance and mark as RECONCILED"
                  color={reconcileData.notes?.trim() ? 'success' : 'warning'}
                  focused
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReconcileDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleReconcile}
              color={reconcileData.notes?.trim() ? 'success' : 'primary'}
            >
              {reconcileData.notes?.trim() ? 'Reconcile & Close' : 'Update Values'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reconcile with Deposits Results Dialog */}
        <Dialog
          open={reconcileDepositsDialogOpen}
          onClose={() => setReconcileDepositsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Reconciliation with Settlement Deposits</DialogTitle>
          <DialogContent>
            {reconcileDepositsResult && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={4}>
                    <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                      <CardContent>
                        <Typography variant="h6">{reconcileDepositsResult.reconciled || 0}</Typography>
                        <Typography variant="body2">Reconciled</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                      <CardContent>
                        <Typography variant="h6">{reconcileDepositsResult.variance || 0}</Typography>
                        <Typography variant="body2">Variance</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ bgcolor: 'grey.300' }}>
                      <CardContent>
                        <Typography variant="h6">{reconcileDepositsResult.skipped || 0}</Typography>
                        <Typography variant="body2">No Deposits</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        <TableCell>Canonical Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Our Dispensed</TableCell>
                        <TableCell align="right">Deposit Dispensed</TableCell>
                        <TableCell align="right">Variance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reconcileDepositsResult.summary?.details?.map((detail, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{detail.locationName}</TableCell>
                          <TableCell>{detail.canonicalName}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={detail.status}
                              color={detail.status === 'RECONCILED' ? 'success' : detail.status === 'VARIANCE' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {detail.ourTotals ? formatCurrency(detail.ourTotals.dispensed) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {detail.depositTotals ? formatCurrency(detail.depositTotals.dispensed) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: detail.variance?.dispensed > 0 ? 'error.main' : detail.variance?.dispensed < 0 ? 'warning.main' : 'inherit' }}>
                            {detail.variance ? formatCurrency(detail.variance.dispensed) : detail.message || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReconcileDepositsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Batch Reconciliation Details Dialog */}
        <Dialog
          open={batchDetailsDialogOpen}
          onClose={() => {
            setBatchDetailsDialogOpen(false);
            setBatchDetails(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Batch Reconciliation Details
            {selectedBatch && (
              <Typography variant="body2" color="text.secondary">
                {selectedBatch.location_name} - {selectedBatch.settlement_date?.split('T')[0]}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {batchDetailsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : batchDetails ? (
              <Box>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={3}>
                    <Card sx={{ bgcolor: 'grey.100' }}>
                      <CardContent>
                        <Typography variant="h5">{batchDetails.summary?.processorTotal || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">Processor Total</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                      <CardContent>
                        <Typography variant="h5">{batchDetails.summary?.matched || 0}</Typography>
                        <Typography variant="body2">Matched</Typography>
                        <Typography variant="caption">
                          {formatCurrency(batchDetails.summary?.matchedDispensed)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                      <CardContent>
                        <Typography variant="h5">{batchDetails.summary?.missing || 0}</Typography>
                        <Typography variant="body2">Missing (Proc has, we don't)</Typography>
                        <Typography variant="caption">
                          {formatCurrency(batchDetails.summary?.missingDispensed)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{
                      bgcolor: batchDetails.summary?.extraWithDispensed > 0 ? 'warning.light' : 'grey.200',
                      color: batchDetails.summary?.extraWithDispensed > 0 ? 'warning.contrastText' : 'text.secondary'
                    }}>
                      <CardContent>
                        <Typography variant="h5">
                          {batchDetails.summary?.extraWithDispensed || 0}
                          {batchDetails.summary?.extraDeclined > 0 && (
                            <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                              (+{batchDetails.summary?.extraDeclined} declined)
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="body2">Extra w/ Dispensed</Typography>
                        <Typography variant="caption">
                          {formatCurrency(batchDetails.summary?.extraDispensed)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Added transactions info */}
                {batchDetails.added && batchDetails.added.length > 0 && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Successfully added {batchDetails.added.length} missing transactions to database.
                  </Alert>
                )}

                {/* Location mapping info */}
                {batchDetails.locationNames && batchDetails.locationNames.length > 1 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Location aliases searched:</strong> {batchDetails.locationNames.join(', ')}
                    </Typography>
                    {batchDetails.ghTerminals && batchDetails.ghTerminals.length > 0 && (
                      <Typography variant="body2">
                        <strong>GH Terminals:</strong> {batchDetails.ghTerminals.join(', ')}
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* Missing Transactions Table */}
                {batchDetails.missingTransactions && batchDetails.missingTransactions.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" color="error.main">
                        Missing Transactions ({batchDetails.missingTransactions.length})
                        <Tooltip title="Transactions the processor has that we don't have in our system. Click '+' to add them.">
                          <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        </Tooltip>
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={addingMissing ? <CircularProgress size={20} color="inherit" /> : <PlaylistAddIcon />}
                        onClick={handleAddMissing}
                        disabled={addingMissing}
                      >
                        {addingMissing ? 'Adding...' : '+ Add Missing to Database'}
                      </Button>
                    </Box>
                    <TableContainer component={Paper} sx={{ maxHeight: 250 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Processor Terminal</TableCell>
                            <TableCell>Date/Time</TableCell>
                            <TableCell>Card Last 4</TableCell>
                            <TableCell align="right">Dispensed</TableCell>
                            <TableCell align="right">Surcharge</TableCell>
                            <TableCell>RRN</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Info</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {batchDetails.missingTransactions.map((txn, idx) => (
                            <TableRow key={txn.id || idx} sx={{ bgcolor: 'rgba(244,67,54,0.1)' }}>
                              <TableCell>{txn.processorTerminalId}</TableCell>
                              <TableCell>{new Date(txn.datetime).toLocaleString()}</TableCell>
                              <TableCell>****{txn.panLastFour}</TableCell>
                              <TableCell align="right">{formatCurrency(txn.dispensed)}</TableCell>
                              <TableCell align="right">{formatCurrency(txn.surcharge)}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{txn.rrn || '-'}</TableCell>
                              <TableCell>{txn.transactionType}</TableCell>
                              <TableCell align="center">
                                <Tooltip title={
                                  <Box>
                                    <Typography variant="body2"><strong>Processor has this, we don't</strong></Typography>
                                    <Typography variant="caption">
                                      ID: {txn.id}<br/>
                                      Terminal: {txn.processorTerminalId}<br/>
                                      RRN: {txn.rrn || 'N/A'}<br/>
                                      Amount: {formatCurrency(txn.dispensed)}
                                    </Typography>
                                    {txn.explanation && (
                                      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                        Note: {txn.explanation}
                                      </Typography>
                                    )}
                                  </Box>
                                }>
                                  <IconButton size="small">
                                    <InfoOutlinedIcon fontSize="small" color="info" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Extra Transactions Table */}
                {batchDetails.extraTransactions && batchDetails.extraTransactions.length > 0 && (
                  <Box>
                    <Typography variant="h6" color={batchDetails.summary?.extraWithDispensed > 0 ? 'warning.main' : 'text.secondary'} sx={{ mb: 1 }}>
                      Extra Transactions ({batchDetails.extraTransactions.length})
                      <Tooltip title="Transactions we have that the processor doesn't. Usually declined/reversed transactions.">
                        <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                      </Tooltip>
                      {batchDetails.summary?.extraWithDispensed === 0 && (
                        <Chip size="small" label="All Declined/Reversed" color="success" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {batchDetails.summary?.extraWithDispensed > 0
                        ? `${batchDetails.summary.extraWithDispensed} with dispensed amounts need investigation`
                        : 'All are declined/reversed ($0 dispensed) - no action needed'}
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 250 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Terminal ID</TableCell>
                            <TableCell>Date/Time</TableCell>
                            <TableCell>Card Last 4</TableCell>
                            <TableCell align="right">Dispensed</TableCell>
                            <TableCell align="right">Surcharge</TableCell>
                            <TableCell>RRN</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Info</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {batchDetails.extraTransactions.map((txn, idx) => (
                            <TableRow
                              key={txn.id || idx}
                              sx={{
                                bgcolor: parseFloat(txn.dispensed) > 0 ? 'rgba(255,152,0,0.2)' : 'rgba(0,0,0,0.02)'
                              }}
                            >
                              <TableCell>{txn.terminalId}</TableCell>
                              <TableCell>{txn.datetime ? new Date(txn.datetime).toLocaleString() : '-'}</TableCell>
                              <TableCell>{txn.panLastFour ? `****${txn.panLastFour}` : '-'}</TableCell>
                              <TableCell align="right" sx={{ color: parseFloat(txn.dispensed) > 0 ? 'warning.main' : 'text.secondary' }}>
                                {formatCurrency(txn.dispensed)}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(txn.surcharge)}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{txn.rrn || '-'}</TableCell>
                              <TableCell>{txn.transactionType}</TableCell>
                              <TableCell align="center">
                                <Tooltip title={
                                  <Box>
                                    <Typography variant="body2">
                                      <strong>{parseFloat(txn.dispensed) > 0 ? 'We have this, processor doesn\'t' : 'Declined/Reversed'}</strong>
                                    </Typography>
                                    <Typography variant="caption">
                                      ID: {txn.id}<br/>
                                      Terminal: {txn.terminalId}<br/>
                                      RRN: {txn.rrn || 'N/A'}<br/>
                                      Response: {txn.responseCode} - {txn.responseDesc || 'Unknown'}<br/>
                                      Amount: {formatCurrency(txn.dispensed)}
                                    </Typography>
                                  </Box>
                                }>
                                  <IconButton size="small">
                                    <InfoOutlinedIcon
                                      fontSize="small"
                                      color={parseFloat(txn.dispensed) > 0 ? 'warning' : 'success'}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Reconciliation status message */}
                {batchDetails.summary?.missing === 0 && batchDetails.summary?.extraWithDispensed === 0 && (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    <strong>Fully Reconciled</strong> - All processor transactions matched.
                    {batchDetails.summary?.extraDeclined > 0 && (
                      <span> ({batchDetails.summary.extraDeclined} declined/reversed transactions in our system are expected)</span>
                    )}
                  </Alert>
                )}
              </Box>
            ) : (
              <Typography color="text.secondary">No data available</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setBatchDetailsDialogOpen(false);
              setBatchDetails(null);
            }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default SettlementBatches;
